/**
 * Write One-Step Inequalities generator (6.PAFR.1.2).
 *
 * Given a word phrase describing a bound ("at least", "no more than", "more
 * than", "fewer than"), choose the inequality that models it. The correct
 * relation and its symbol are chosen first; distractors are the other three
 * relation symbols against the same value, so the graded choice id is always
 * one of the presented options and differs only in the symbol.
 *
 * Families (by phrase type):
 *   F1: "at least" / "minimum"      (>=)
 *   F2: "at most" / "no more than"  (<=)
 *   F3: "more than" / "greater than" (>)
 *   F4: "fewer than" / "less than"  (<)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type { InequalityOp } from "../engine/answer";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "write-inequalities" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

const OP_FAMILY: Record<Family, InequalityOp> = {
  F1: ">=",
  F2: "<=",
  F3: ">",
  F4: "<",
};

const PHRASES: Record<Family, readonly string[]> = {
  F1: ["at least", "a minimum of", "no less than"],
  F2: ["at most", "no more than", "a maximum of"],
  F3: ["more than", "greater than"],
  F4: ["fewer than", "less than"],
};

const OP_LATEX: Record<InequalityOp, string> = {
  ">=": "\\geq",
  "<=": "\\leq",
  ">": ">",
  "<": "<",
};

interface Cfg {
  readonly families: readonly Family[];
  readonly valueMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], valueMax: 12 },
  standard: { families: ["F1", "F2", "F3", "F4"], valueMax: 20 },
  hard: { families: ["F1", "F2", "F3", "F4"], valueMax: 40 },
  challenge: { families: ["F1", "F2", "F3", "F4"], valueMax: 60 },
};

const VARS = ["x", "n", "t"] as const;

const CONTEXTS: readonly ((v: string, phrase: string, val: string) => string)[] = [
  (v, phrase, val) => `A ride requires riders to be ${phrase} ${val} years old. Let ${v} be a rider's age. Which inequality models this?`,
  (v, phrase, val) => `A team can have ${phrase} ${val} players. Let ${v} be the number of players. Which inequality models this?`,
  (v, phrase, val) => `You must score ${phrase} ${val} points to pass. Let ${v} be the score. Which inequality models this?`,
];

export class WriteInequalitiesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const op = OP_FAMILY[family];
      const v = rng.pick(VARS);
      const value = rat(rng.int(2, cfg.valueMax));
      const phrase = rng.pick(PHRASES[family]);
      const context = rng.pick(CONTEXTS);
      const promptText = context(v, phrase, value.toString());

      const allOps: InequalityOp[] = [">=", "<=", ">", "<"];
      const withIds = allOps.map((o, i) => ({
        id: `c${i}`,
        latex: `${v} ${OP_LATEX[o]} ${value.toLatex()}`,
        correct: o === op,
      }));
      const shuffled = rng.shuffle(withIds);
      const choices: Choice[] = shuffled.map((o) => ({ id: o.id, latex: o.latex }));
      const correct = shuffled.find((o) => o.correct);
      if (!correct) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: `${v} ${OP_LATEX[op]} ${value.toLatex()}` },
        answerType: "choice",
        answer: { kind: "choice", value: correct.id },
        steps: [
          {
            description: `"${phrase}" translates to the symbol ${OP_LATEX[op]}.`,
            latex: `${v} ${OP_LATEX[op]} ${value.toLatex()}`,
          },
        ],
        hints: [
          { level: 1, text: '"At least" and "at most" include the value (≥, ≤); "more/fewer than" do not (>, <).' },
          { level: 2, text: "Decide the direction, then decide whether the boundary value is included." },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const writeInequalities = new WriteInequalitiesGenerator();
